import axios from "axios";

// Sandbox mode: anonymous read-only demo session for visitors coming from the
// commercial website. The token itself is an API-minted sandbox JWT (no Zitadel
// user), cached by lib/appTokenStore.ts.
//
// The whole demo session — flag, clock and lead marker — is deliberately
// scoped to sessionStorage, i.e. to the tab that opened /sandbox. The demo
// runs on the same origin as the real app, so an origin-wide (localStorage)
// flag made a logged-in user who opened /sandbox in a second tab land in the
// demo everywhere on their next reload.
//
// NOTE: this module must not import from lib/api.ts — the API client imports
// these helpers for its 401-retry interceptor, so it uses raw axios instead.

const SANDBOX_FLAG_KEY = "sandboxMode";
const SANDBOX_START_KEY = "sandboxStartTime";
const SANDBOX_LEAD_KEY = "sandboxLeadCaptured";

// These keys used to live in localStorage. Drop leftovers on load so a flag
// written by the previous build can't keep pulling other tabs into the demo.
try {
  [SANDBOX_FLAG_KEY, SANDBOX_START_KEY, SANDBOX_LEAD_KEY].forEach((key) =>
    localStorage.removeItem(key),
  );
} catch {
  /* ignore */
}

export function isSandboxMode(): boolean {
  try {
    return sessionStorage.getItem(SANDBOX_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

export function enterSandbox(): void {
  try {
    sessionStorage.setItem(SANDBOX_FLAG_KEY, "1");
    // Record the start time only once — re-mints don't reset the clock.
    if (!sessionStorage.getItem(SANDBOX_START_KEY)) {
      sessionStorage.setItem(SANDBOX_START_KEY, Date.now().toString());
    }
  } catch {
    /* ignore */
  }
}

export function exitSandbox(): void {
  try {
    sessionStorage.removeItem(SANDBOX_FLAG_KEY);
    sessionStorage.removeItem(SANDBOX_START_KEY);
    sessionStorage.removeItem(SANDBOX_LEAD_KEY);
  } catch {
    /* ignore */
  }
}

/** Milliseconds elapsed since the sandbox session started (0 if unknown). */
export function getSandboxElapsedMs(): number {
  try {
    const raw = sessionStorage.getItem(SANDBOX_START_KEY);
    return raw ? Date.now() - parseInt(raw, 10) : 0;
  } catch {
    return 0;
  }
}

/** True once the visitor has submitted their email via the demo gate. */
export function hasSandboxLead(): boolean {
  try {
    return sessionStorage.getItem(SANDBOX_LEAD_KEY) === "1";
  } catch {
    return false;
  }
}

export function markSandboxLead(): void {
  try {
    sessionStorage.setItem(SANDBOX_LEAD_KEY, "1");
  } catch {
    /* ignore */
  }
}

const configuredApi = import.meta.env.VITE_API_BASE_URL as string | undefined;
const apiBase =
  configuredApi && configuredApi.trim()
    ? configuredApi
    : typeof window !== "undefined"
      ? window.location.origin
      : "";

/**
 * Mints a fresh anonymous sandbox session token from the public endpoint and
 * marks sandbox mode active. Throws when the sandbox is disabled server-side
 * or rate-limited.
 */
export async function mintSandboxSession(): Promise<string> {
  const res = await axios.post(`${apiBase}/v2/sandbox/session`);
  const token: string | undefined = res.data?.token;
  if (!token) throw new Error("Sandbox session unavailable");
  enterSandbox();
  return token;
}

/**
 * Submits a visitor email lead to the server. Marks the gate as completed
 * locally so it is not shown again for this sandbox session.
 */
export async function submitSandboxLead(email: string): Promise<void> {
  await axios.post(`${apiBase}/v2/sandbox/lead`, { email });
  markSandboxLead();
}
